import { Button } from '@src/design-system/button';
import { createRoute } from '@src/core/router/paths';
import { Form, Formik, type FormikProps } from 'formik';
import { ArrowLeft } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import EditRegistrationInfo from './edit-registration-info';
import EditAdditionalInfo from './edit-additional-info';
import EditAddressInfo from './edit-address-info';
import EditEmergencyContactInformation from './edit-emergency-contact-information';
import { useEffect, useRef } from 'react';
import type { TEmergencyContactInfo } from '@shared/types/member';
import type { TAddress } from '@shared/types/organization';
import {
  additionalInfoSchema,
  addressSchema,
  contactInfoSchema,
  emergencyContactSchema,
  generateAdditionalInfoInitialValue,
  generateAddressFormInitialValue,
  generateContactInfo,
  generateEmergencyContactInfoInitialValue,
  generatePersonalInfoInitialValue,
  type IAdditionalInfo,
  type IPersonalInfo,
  personalInfoSchema,
  type TContactInfoValues,
} from './constants';
import { useEditUserAddress } from '../../services/edit-address';
import { useEditUserEContactInfo } from '../../services/edit-emergency-contact-info';
import EditContactInfo from './edit-contact-info';
import { useEditUserContactInfo } from '../../services/edit-contact-info';
import memberApi from '@shared/services/member-api';
import userApi from '@shared/services/user-api';

const EditPartner = () => {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const { data: userData } = memberApi.useFetchOne({ id: partnerId });
  const formRegistrationRef = useRef<FormikProps<IPersonalInfo>>(null);
  const formAddressInfoRef = useRef<FormikProps<TAddress>>(null);
  const formContactInfoRef = useRef<FormikProps<TContactInfoValues>>(null);
  const formAdditionalInfoRef = useRef<FormikProps<IAdditionalInfo>>(null);
  const formEmergencyContactInfoRef =
    useRef<FormikProps<TEmergencyContactInfo>>(null);

  const {
    mutateAsync: editUser,
    isPending: editUserPending,
    isSuccess: editUserSuccess,
  } = userApi.useUpdate();
  const {
    mutateAsync: editAddress,
    isPending: editAddressPending,
    isSuccess: editAddressSuccess,
  } = useEditUserAddress();
  const {
    mutateAsync: editUserEContactInfo,
    isPending: editEContactInfoPending,
    isSuccess: editEContactInfoSuccess,
  } = useEditUserEContactInfo();
  const {
    mutateAsync: editUserContactInfo,
    isPending: editContactInfoPending,
    isSuccess: editContactInfoSuccess,
  } = useEditUserContactInfo();
  const {
    mutateAsync: editAdditionalInfo,
    isPending: editAdditionalInfoPending,
    isSuccess: editAdditionalInfoSuccess,
  } = memberApi.useUpdate();

  const handleRegistrationSubmit = (values: IPersonalInfo) => {
    if (userData?.user.id) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { avatar, ...rest } = values;
      editUser({ ...rest, id: userData?.user.id });
    }
  };

  const handleAdditionalSubmit = (values: IAdditionalInfo) => {
    if (userData?.id) {
      editAdditionalInfo({ ...values, id: userData?.id });
    }
  };

  const handleAddressSubmit = (values: TAddress) => {
    editAddress({ ...values, userId: +partnerId! });
  };

  const handleEmergencyContactSubmit = (values: TEmergencyContactInfo) => {
    editUserEContactInfo({ ...values, userId: +partnerId! });
  };

  const handleContactInfoSubmit = (values: TContactInfoValues) => {
    editUserContactInfo({
      ...values,
      userId: +partnerId!,
      id: userData?.contact_info?.id || 0,
    });
  };

  const handleSharedSubmit = async () => {
    if (formRegistrationRef.current?.dirty) {
      await formRegistrationRef?.current?.submitForm();
    }
    if (formAdditionalInfoRef.current?.dirty) {
      await formAdditionalInfoRef.current.submitForm();
    }
    if (formAddressInfoRef.current?.dirty) {
      await formAddressInfoRef.current.submitForm();
    }
    if (formEmergencyContactInfoRef.current?.dirty) {
      await formEmergencyContactInfoRef.current.submitForm();
    }
    if (formContactInfoRef.current?.dirty) {
      await formContactInfoRef.current.submitForm();
    }
  };

  useEffect(() => {
    if (
      editUserSuccess ||
      editAddressSuccess ||
      editEContactInfoSuccess ||
      editAdditionalInfoSuccess ||
      editContactInfoSuccess
    ) {
      navigate(createRoute(`/users/partner/${partnerId}`));
    }
  }, [
    editUserSuccess,
    editAddressSuccess,
    editEContactInfoSuccess,
    editAdditionalInfoSuccess,
    editContactInfoSuccess,
    navigate,
    partnerId,
  ]);

  if (!userData) return null;

  return (
    <div className='relative bg-primary-30'>
      <div className='flex justify-between bg-white items-center p-6'>
        <h1 className='text-2xl font-semibold text-gray-1000 flex items-center'>
          <Button
            variant='primaryLink'
            size={'iconSm'}
            onClick={() => navigate(createRoute(`/users/partner/${partnerId}`))}
          >
            <ArrowLeft />
          </Button>
          <p className='text-2xl font-semibold text-gray-1000'>
            Edit Personal Info
          </p>
        </h1>
      </div>
      <div className='p-5'>
        <Formik
          key={1}
          initialValues={
            generatePersonalInfoInitialValue(userData) as IPersonalInfo
          }
          validationSchema={personalInfoSchema}
          onSubmit={(values) => handleRegistrationSubmit(values)}
          innerRef={formRegistrationRef}
        >
          <Form>
            <EditRegistrationInfo />
          </Form>
        </Formik>
        <Formik
          key={2}
          initialValues={
            generateAdditionalInfoInitialValue(userData) as IAdditionalInfo
          }
          validationSchema={additionalInfoSchema}
          onSubmit={(values) => handleAdditionalSubmit(values)}
          innerRef={formAdditionalInfoRef}
        >
          <Form>
            <EditAdditionalInfo />
          </Form>
        </Formik>
        <Formik
          key={3}
          initialValues={generateAddressFormInitialValue(userData) as TAddress}
          validationSchema={addressSchema}
          onSubmit={(values) => handleAddressSubmit(values)}
          innerRef={formAddressInfoRef}
        >
          <Form>
            <EditAddressInfo />
          </Form>
        </Formik>
        <Formik
          key={4}
          initialValues={
            generateContactInfo({
              personal_email: userData.contact_info?.personal_email,
              personal_phone_number:
                userData.contact_info?.personal_phone_number,
            }) as TContactInfoValues
          }
          validationSchema={contactInfoSchema}
          onSubmit={(values) => handleContactInfoSubmit(values)}
          innerRef={formContactInfoRef}
        >
          <Form>
            <EditContactInfo />
          </Form>
        </Formik>
        <Formik
          key={5}
          initialValues={
            generateEmergencyContactInfoInitialValue(
              userData
            ) as TEmergencyContactInfo
          }
          validationSchema={emergencyContactSchema}
          onSubmit={(values) => handleEmergencyContactSubmit(values)}
          innerRef={formEmergencyContactInfoRef}
        >
          <Form>
            <EditEmergencyContactInformation />
          </Form>
        </Formik>
      </div>
      <div className='bg-white border-t-2 z-50 border-pale-border inset-x-0 bottom-0 sticky'>
        <div className='flex items-center py-4 px-5 mx-auto justify-between'>
          <Button
            variant='primaryOutline'
            onClick={() => navigate(createRoute(`/users/partner/${partnerId}`))}
          >
            Cancel
          </Button>

          <Button
            type='button'
            onClick={handleSharedSubmit}
            isLoading={
              editUserPending ||
              editAddressPending ||
              editEContactInfoPending ||
              editAdditionalInfoPending ||
              editContactInfoPending
            }
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditPartner;
